import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import type { SchemaField, SchemaFieldTypes } from "../../../../helpers/format-databases";
import makeConfirmDialog from "../../../../ui/components/utils/ConfirmDialog/ConfirmDialog";
import OurDataTable, {
  type ColumnType,
} from "../../../../ui/components/utils/OurDataTable/OurDataTable";
import { syncOpenModalDialog } from "../../../../ui/shared/global-tools/dialog-system";
import EditIcon from "../../../icons/EditIcon";
import TrashIcon from "../../../icons/TrashIcon";
import makeEditFieldDialog, {
  type EditFieldDialogParameter,
} from "./EditFieldDialog/EditFieldDialog";
import makeViewMoreOptionsDialog, {
  type ViewMoreOptionsDialogParameter,
} from "./ViewMoreOptionsDialog";

export interface SchemaFieldDataTableProps {
  className: string;
  schemaFields: SchemaField[];
  schemaFieldTypes: SchemaFieldTypes;
  onChange?: OnSchemaFieldsChange;
}

export type OnSchemaFieldsChange = (newFields: SchemaField[]) => void;

export default function SchemaFieldDataTable({
  className,
  schemaFields,
  schemaFieldTypes,
  onChange,
}: SchemaFieldDataTableProps) {
  const handleFieldDeletion = (name: string, onChange: OnSchemaFieldsChange) => {
    const index = schemaFields.findIndex((f) => f.name === name);
    if (index !== -1) {
      schemaFields.splice(index, 1);
    }
    onChange([...schemaFields]);
  };
  const handleFieldEdition = (
    name: string,
    {
      editedField,
      onChange,
    }: {
      editedField: SchemaField;
      onChange: OnSchemaFieldsChange;
    },
  ) => {
    const newSchemaFields = schemaFields.map((f) => {
      if (f.name === name) return editedField;
      return f;
    });
    onChange(newSchemaFields);
  };

  const editField = onChange
    ? (field: SchemaField) =>
        syncOpenModalDialog<EditFieldDialogParameter>(makeEditFieldDialog, {
          edit: (editedField) =>
            handleFieldEdition(field.name, {
              editedField,
              onChange,
            }),
          schemaFieldTypes,
          schemaFields,
          fieldToEdit: field,
        })
    : undefined;
  const deleteField = onChange
    ? (field: SchemaField) =>
        syncOpenModalDialog(makeConfirmDialog, {
          onConfirm: () => handleFieldDeletion(field.name, onChange),
          warningMessage: (
            <>
              Voulez-vous supprimer le champ <b>{field.label}</b> ?
            </>
          ),
        })
    : undefined;

  const requiredBodyTemplate = (field: SchemaField) => (
    <Badge value={field.required ? "Oui" : "Non"} severity="info" />
  );
  const optionsBodyTemplate = (field: SchemaField) => {
    const options = field.options ?? [];
    const firstTwoOptions = options.slice(0, 2);
    return (
      <div className="flex">
        {firstTwoOptions.map((o) => (
          <Badge key={o.key} value={o.value} severity="info" className="mr-1" />
        ))}
        {options.length > 2 && (
          <Button
            icon="pi pi-eye"
            style={{ padding: "2px 0px" }}
            onClick={() =>
              syncOpenModalDialog<ViewMoreOptionsDialogParameter>(makeViewMoreOptionsDialog, {
                options,
                fieldName: field.name,
              })
            }
          />
        )}
      </div>
    );
  };
  const typeBodyTemplate = (field: SchemaField) =>
    schemaFieldTypes.get(field.type) ?? "Chaîne de caractères";
  const editActionBodyTemplate = (field: SchemaField, editField: (field: SchemaField) => void) => (
    <Button
      size="small"
      icon={<EditIcon />}
      className="p-outlined-primary IconOnlyBtn"
      rounded
      text
      onClick={() => editField(field)}
    />
  );
  const deleteActionBodyTemplate = (
    field: SchemaField,
    deleteField: (field: SchemaField) => void,
  ) => (
    <Button
      size="small"
      icon={<TrashIcon />}
      severity="danger"
      rounded
      text
      onClick={() => deleteField(field)}
      className="IconOnlyBtn"
    />
  );

  const columns: ColumnType<SchemaField>[] = [
    {
      field: "label",
      header: "Libellé du champ",
      style: { minWidth: "140px" },
    },
    {
      field: "name",
      header: "Nom du champ",
      style: { minWidth: "140px" },
    },
    {
      field: "type",
      header: "Type du champ",
      style: { minWidth: "140px" },
      body: typeBodyTemplate,
    },
    {
      field: "required",
      header: "Est obligatoire?",
      style: { minWidth: "140px" },
      body: requiredBodyTemplate,
    },
    {
      field: "options",
      header: "Options",
      body: optionsBodyTemplate,
    },
    {
      field: "length",
      header: "Longueur et précision",
      style: { minWidth: "180px" },
    },
    {
      field: "default",
      header: "Valeur par défaut",
      style: { minWidth: "140px" },
    },
  ];
  if (deleteField) {
    columns.unshift({
      field: "delete",
      header: "",
      style: { minWidth: "50px" },
      body: (e) => deleteActionBodyTemplate(e, deleteField),
    });
  }
  if (editField) {
    columns.unshift({
      field: "edit",
      header: "",
      style: { minWidth: "50px" },
      body: (e) => editActionBodyTemplate(e, editField),
    });
  }
  return (
    <OurDataTable<SchemaField>
      className={className}
      data={schemaFields}
      columns={columns}
      options={{
        tableStyle: { minWidth: "50rem" },
        rows: 10,
      }}
    />
  );
}
